import { io, Socket } from 'socket.io-client';
import {
  useEffect,
  ReactNode,
  useContext,
  ReactElement,
  createContext,
  useRef,
  useState,
  useCallback,
} from 'react';
import AuthContext from './AuthContext';
import BillingContext from './BillingContext';
import { useNotification } from 'context/NotificationContext';
import { logger, ErrorType } from '../utils/logger';

const STRIPE_UPDATE = 'stripe:update';
const ACCOUNT_UPDATE = 'account:update';

interface SocketProviderProps {
  children: ReactNode;
}

const SocketContext = createContext<{
  actions: {};
  data: {};
}>({
  actions: {},
  data: {},
});

/**
 * SocketProvider manages real-time WebSocket connections for live system updates.
 * 
 * This provider handles:
 * - Persistent WebSocket connection to the backend notification service  
 * - Automatic authentication with user tokens
 * - Connection lifecycle management (connect, disconnect, reconnect)
 * - Real-time event distribution throughout the application
 * 
 * Key Features:
 * - Auto-connects when user is authenticated
 * - Auto-disconnects on logout to prevent unauthorized access
 * - Handles connection errors and provides fallback behavior
 * - Enables real-time notifications for call status, system updates, etc.
 * 
 * Integration Points:
 * - Backend WebSocket service for live notifications
 * - Authentication context for secure connection establishment
 * - Various components that need real-time updates (Dashboard, Call Status, etc.)
 * 
 * @example
 * // Components can access socket for real-time features:
 * const { socket } = useContext(SocketContext);
 * socket?.on('call_update', handleCallUpdate);
 * 
 * @see {@link https://socket.io/docs/v4/client-api/} Socket.IO Client Documentation
 */
export function SocketProvider(props: SocketProviderProps): ReactElement {
  const { children } = props;
  const {
    data: { authToken, userFetched },
    actions: { isLoggedIn, getUser },
  } = useContext(AuthContext);
  const {
    actions: { syncStripe },
  } = useContext(BillingContext);
  const { showSuccess } = useNotification();
  
  const socketRef = useRef<Socket | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const connectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasAttemptedConnection = useRef(false);
  const lastTokenRef = useRef<string | null>(null);

  // Stable callback to prevent effect re-runs
  const handleStripeUpdate = useCallback(() => {
    getUser();
    showSuccess('Your account has been updated');
  }, [getUser, showSuccess]);

  const handleAccountUpdate = useCallback(() => {
    getUser();
  }, [getUser]);

  const connectSocket = useCallback(() => {
    const token = authToken?.access_token;
    
    // Skip if no token or already connecting/connected
    if (!token || isConnecting || socketRef.current?.connected) {
      return;
    }

    // Skip if user data hasn't been fetched/validated yet
    if (!userFetched) {
      logger.info('Waiting for user data validation before WebSocket connection');
      return;
    }

    // Skip if we've already attempted connection with this token
    if (hasAttemptedConnection.current && lastTokenRef.current === token) {
      return;
    }

    // Mark that we're attempting connection with this token
    hasAttemptedConnection.current = true;
    lastTokenRef.current = token;
    setIsConnecting(true);

    logger.info('Initiating WebSocket connection');

    // Create socket instance
    const socket = io(process.env.REACT_APP_WS_URL, {
      path: '/api/v1/socket.io',
      transports: ['websocket', 'polling'],
      autoConnect: false,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 3, // Reduced from 5
      timeout: 10000,
      auth: {
        token: token
      }
    });

    socketRef.current = socket;

    // Set up event listeners
    socket.on('connect', () => {
      setIsConnecting(false);
      logger.info('WebSocket connected successfully');
      socket.emit('join', { token: token });
      syncStripe();
    });

    socket.on('connect_error', (error) => {
      setIsConnecting(false);
      logger.error('WebSocket connection failed', error, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'SocketProvider'
      });
    });

    socket.on('disconnect', (reason) => {
      setIsConnecting(false);
      // Reset connection attempt flag on disconnect to allow reconnection
      hasAttemptedConnection.current = false;
      logger.warn('WebSocket disconnected', undefined, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'SocketProvider',
        metadata: { reason }
      });
    });

    socket.on('error', (error) => {
      logger.error('WebSocket error occurred', error, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'SocketProvider'
      });
    });

    socket.on('reconnect', () => {
      logger.info('WebSocket reconnected');
    });

    socket.on('reconnect_error', (error) => {
      logger.error('WebSocket reconnection failed', error, {
        errorType: ErrorType.NETWORK_ERROR,
        component: 'SocketProvider'
      });
    });

    socket.on(STRIPE_UPDATE, handleStripeUpdate);
    socket.on(ACCOUNT_UPDATE, handleAccountUpdate);

    // Connection timeout
    connectTimeoutRef.current = setTimeout(() => {
      if (!socket.connected) {
        logger.warn('WebSocket connection timed out');
        setIsConnecting(false);
        hasAttemptedConnection.current = false; // Allow retry
      }
    }, 15000);

    // Connect
    socket.connect();
  }, [authToken?.access_token, userFetched, isConnecting, syncStripe, handleStripeUpdate, handleAccountUpdate]);

  const disconnectSocket = useCallback(() => {
    if (connectTimeoutRef.current) {
      clearTimeout(connectTimeoutRef.current);
      connectTimeoutRef.current = null;
    }
    
    if (socketRef.current) {
      logger.info('Disconnecting WebSocket');
      socketRef.current.removeAllListeners();
      socketRef.current.disconnect();
      socketRef.current = null;
    }
    
    setIsConnecting(false);
    hasAttemptedConnection.current = false;
    lastTokenRef.current = null;
  }, []);

  useEffect(() => {
    // Only connect when user is logged in AND user data has been fetched/validated
    if (isLoggedIn() && authToken?.access_token && userFetched) {
      connectSocket();
    } else {
      disconnectSocket();
    }

    return () => {
      if (connectTimeoutRef.current) {
        clearTimeout(connectTimeoutRef.current);
      }
    };
  }, [authToken?.access_token, userFetched, isLoggedIn, connectSocket, disconnectSocket]);

  // Cleanup on unmount
  useEffect(() => {
    return disconnectSocket;
  }, [disconnectSocket]);

  return (
    <SocketContext.Provider
      value={{
        actions: {},
        data: {},
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}
export default SocketContext;
